import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { RecurringInvoice, RecurringInvoicePosition, Client } from '../types';
import recurringInvoiceService from '../services/recurringInvoiceService';
import clientService, { composeRecipientAddress } from '../services/clientService';
import { PageHeader, Badge, Spinner, ModalOverlay } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * Serienrechnungen: Vorlagen, die ein täglicher Job fällig stellt — Rechnung
 * erzeugen, festschreiben, verschicken. Hier werden sie gepflegt.
 */

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const centsToInput = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');
const inputToCents = (v: string): number => {
  const n = parseFloat(v.replace(/[^0-9,.-]/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : Math.round(n * 100);
};

const formatDate = (iso?: string | null): string =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('de-DE') : '—';

const rhythm = (r: RecurringInvoice): string =>
  r.intervalMonths === 1 ? `monatlich am ${r.dayOfMonth}.`
    : r.intervalMonths === 3 ? `vierteljährlich am ${r.dayOfMonth}.`
    : r.intervalMonths === 12 ? `jährlich am ${r.dayOfMonth}.`
    : `alle ${r.intervalMonths} Monate am ${r.dayOfMonth}.`;

const netCents = (r: RecurringInvoice): number =>
  r.positions.reduce((sum, p) => sum + p.unitPriceCents * p.quantity, 0);

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const emptySeries = (): RecurringInvoice => ({
  clientId: 0,
  name: '',
  titleTemplate: 'Entwicklungsleistungen {ZEITRAUM}',
  sectionTitle: 'Leistungen',
  introText: 'Wir berechnen Ihnen für die aufgeführten Leistungen den ausgewiesenen Betrag:',
  paymentTerms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
  vatRatePercent: 19,
  intervalMonths: 1,
  dayOfMonth: 15,
  dueDays: 14,
  servicePeriodMonths: 1,
  startDate: todayIso(),
  nextRunDate: todayIso(),
  active: true,
  positions: [{ sortOrder: 1, positionNumber: '1.1', title: '', unitPriceCents: 0, quantity: 1 }],
});

const RecurringInvoices: React.FC = () => {
  usePageTitle('Serienrechnungen');
  const navigate = useNavigate();
  const [series, setSeries] = useState<RecurringInvoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<RecurringInvoice | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [list, clientList] = await Promise.all([
        recurringInvoiceService.getAll(true),
        clientService.getAll(),
      ]);
      setSeries(list);
      setClients(clientList);
    } catch {
      toast.error('Serienrechnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editor) return;
    if (!editor.clientId) { toast.error('Bitte Kunden wählen'); return; }
    if (!editor.name.trim()) { toast.error('Bezeichnung fehlt'); return; }
    if (editor.positions.length === 0) { toast.error('Mindestens eine Position'); return; }
    setSaving(true);
    try {
      if (editor.id) await recurringInvoiceService.update(editor.id, editor);
      else await recurringInvoiceService.create(editor);
      toast.success('Gespeichert');
      setEditor(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (r: RecurringInvoice) => {
    if (!window.confirm(
      `Jetzt eine Rechnung aus „${r.name}" erzeugen, festschreiben und verschicken?`
    )) return;
    try {
      const invoice = await recurringInvoiceService.run(r.id!);
      toast.success(`${invoice.number} verschickt`);
      await load();
      navigate(`/invoices/${invoice.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error ?? 'Lauf fehlgeschlagen');
    }
  };

  const remove = async (r: RecurringInvoice) => {
    if (!window.confirm(`Serie „${r.name}" beenden? Bereits erzeugte Rechnungen bleiben erhalten.`)) return;
    try {
      await recurringInvoiceService.delete(r.id!);
      toast.success('Serie beendet');
      await load();
    } catch {
      toast.error('Beenden fehlgeschlagen');
    }
  };

  const selectClient = (id: number) => setEditor(e => {
    if (!e) return e;
    const client = clients.find(c => c.id === id);
    return {
      ...e,
      clientId: id,
      contactPerson: e.contactPerson?.trim() ? e.contactPerson : (client?.contactPerson ?? null),
      recipientAddress: e.recipientAddress?.trim()
        ? e.recipientAddress
        : (client ? composeRecipientAddress(client) : null),
    };
  });

  const updatePosition = (idx: number, patch: Partial<RecurringInvoicePosition>) =>
    setEditor(e => {
      if (!e) return e;
      const positions = [...e.positions];
      positions[idx] = { ...positions[idx], ...patch };
      return { ...e, positions };
    });

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Serienrechnungen"
        subtitle="Wiederkehrende Rechnungen, die automatisch erzeugt und verschickt werden"
        action={
          <button onClick={() => setEditor(emptySeries())}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
            + Neue Serie
          </button>
        }
      />

      {series.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Noch keine Serie angelegt. Aus einer bestehenden Rechnung geht es am schnellsten:
          Rechnung öffnen → „Serie daraus anlegen".
        </p>
      ) : (
        <div className="space-y-3">
          {series.map(r => (
            <div key={r.id} className="rounded-xl p-4 border"
              style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-bg)' }}>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{r.name}</span>
                    <Badge variant={r.active ? 'green' : 'amber'}>{r.active ? 'Aktiv' : 'Beendet'}</Badge>
                    {r.lastError && <Badge variant="red">Fehler</Badge>}
                  </div>
                  <div className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                    {r.clientName} · {euro(netCents(r))} netto · {rhythm(r)}
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Nächste Rechnung: <strong style={{ color: 'var(--text-primary)' }}>{formatDate(r.nextRunDate)}</strong>
                    {r.endDate && ` · endet ${formatDate(r.endDate)}`}
                    {r.lastInvoiceNumber && ` · zuletzt ${r.lastInvoiceNumber}`}
                  </div>
                  {r.lastError && (
                    <div className="text-xs mt-1" style={{ color: 'var(--badge-red-text)' }}>
                      Letzter Versuch fehlgeschlagen: {r.lastError}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 items-start">
                  <button onClick={() => setEditor(r)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                    style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                    Bearbeiten
                  </button>
                  {r.active && (
                    <button onClick={() => runNow(r)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                      Jetzt ausführen
                    </button>
                  )}
                  {r.active && (
                    <button onClick={() => remove(r)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                      style={{ borderColor: 'var(--badge-red-text)', color: 'var(--badge-red-text)' }}>
                      Beenden
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && (
        <ModalOverlay role="dialog" onKeyDown={(e) => { if (e.key === 'Escape') setEditor(null); }}>
          <div className="rounded-xl p-6 w-full max-w-2xl"
            style={{ background: 'var(--surface-bg)', color: 'var(--text-primary)' }}>
            <h2 className="text-lg font-semibold mb-1 tracking-tight">
              {editor.id ? 'Serie bearbeiten' : 'Neue Serie'}
            </h2>
            <p className="text-xs mb-5" style={{ color: 'var(--text-muted)' }}>
              Die Rechnung wird am Stichtag automatisch erzeugt, festgeschrieben und verschickt.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Kunde *">
                <Select value={editor.clientId} onChange={v => selectClient(Number(v))}>
                  <option value={0}>— wählen —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Bezeichnung (intern) *">
                <Input value={editor.name} onChange={v => setEditor({ ...editor, name: v })}
                  placeholder="OptiVisit Entwicklung monatlich" />
              </Field>
              <Field label="Rechnungstitel *" full>
                <Input value={editor.titleTemplate}
                  onChange={v => setEditor({ ...editor, titleTemplate: v })} />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  Platzhalter: <code>{'{ZEITRAUM}'}</code> → 07/2026, <code>{'{MONAT}'}</code>, <code>{'{JAHR}'}</code>
                  {' '}— jeweils der Beginn des Leistungszeitraums.
                </p>
              </Field>
              <Field label="Rhythmus">
                <Select value={editor.intervalMonths}
                  onChange={v => setEditor({ ...editor, intervalMonths: Number(v) })}>
                  <option value={1}>monatlich</option>
                  <option value={3}>vierteljährlich</option>
                  <option value={6}>halbjährlich</option>
                  <option value={12}>jährlich</option>
                </Select>
              </Field>
              <Field label="Am Tag (1–28)">
                <Input type="number" value={String(editor.dayOfMonth)}
                  onChange={v => setEditor({ ...editor, dayOfMonth: Math.min(28, Math.max(1, Number(v) || 1)) })} />
              </Field>
              <Field label="Nächste Rechnung am">
                <Input type="date" value={editor.nextRunDate}
                  onChange={v => setEditor({ ...editor, nextRunDate: v, startDate: editor.id ? editor.startDate : v })} />
              </Field>
              <Field label="Läuft bis (optional)">
                <Input type="date" value={editor.endDate ?? ''}
                  onChange={v => setEditor({ ...editor, endDate: v || null })} />
              </Field>
              <Field label="Zahlungsziel (Tage)">
                <Input type="number" value={String(editor.dueDays)}
                  onChange={v => setEditor({ ...editor, dueDays: Math.max(0, Number(v) || 0) })} />
              </Field>
              <Field label="Leistungszeitraum (Monate zurück)">
                <Input type="number" value={String(editor.servicePeriodMonths)}
                  onChange={v => setEditor({ ...editor, servicePeriodMonths: Math.max(0, Number(v) || 0) })} />
              </Field>
              <Field label="MwSt (%)">
                <Input type="number" value={String(editor.vatRatePercent)}
                  onChange={v => setEditor({ ...editor, vatRatePercent: Math.max(0, Number(v) || 0) })} />
              </Field>
              <Field label="Empfänger (leer = Rechnungsadresse des Kunden)">
                <Input type="email" value={editor.sendTo ?? ''}
                  onChange={v => setEditor({ ...editor, sendTo: v || null })} />
              </Field>
              <Field label="Ansprechpartner (Anrede)">
                <Input value={editor.contactPerson ?? ''}
                  onChange={v => setEditor({ ...editor, contactPerson: v || null })}
                  placeholder="Herrn Max Mustermann" />
              </Field>
              <Field label="Empfänger-Anschrift" full>
                <Textarea value={editor.recipientAddress ?? ''} rows={4}
                  onChange={v => setEditor({ ...editor, recipientAddress: v || null })} />
              </Field>
              <Field label="Einleitungstext" full>
                <Textarea value={editor.introText ?? ''} rows={3}
                  onChange={v => setEditor({ ...editor, introText: v || null })} />
              </Field>
              <Field label="Zahlungsbedingungen" full>
                <Textarea value={editor.paymentTerms ?? ''} rows={2}
                  onChange={v => setEditor({ ...editor, paymentTerms: v || null })} />
              </Field>
            </div>

            <div className="mt-5">
              <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>
                Positionen
              </label>
              {editor.positions.map((p, idx) => (
                <div key={idx} className="flex flex-wrap gap-2 items-end mb-2">
                  <div className="flex-1 min-w-[180px]">
                    <Input value={p.title} placeholder="Leistung"
                      onChange={v => updatePosition(idx, { title: v })} />
                  </div>
                  <div className="w-28">
                    <Input value={centsToInput(p.unitPriceCents)}
                      onChange={v => updatePosition(idx, { unitPriceCents: inputToCents(v) })} />
                  </div>
                  <div className="w-16">
                    <Input type="number" value={String(p.quantity)}
                      onChange={v => updatePosition(idx, { quantity: Math.max(1, Number(v) || 1) })} />
                  </div>
                  <button onClick={() => setEditor({ ...editor, positions: editor.positions.filter((_, i) => i !== idx) })}
                    className="px-2 py-1.5 text-xs" style={{ color: 'var(--badge-red-text)' }}>✕</button>
                </div>
              ))}
              <button
                onClick={() => setEditor({
                  ...editor,
                  positions: [...editor.positions, {
                    sortOrder: editor.positions.length + 1,
                    positionNumber: `1.${editor.positions.length + 1}`,
                    title: '', unitPriceCents: 0, quantity: 1,
                  }],
                })}
                className="text-xs px-3 py-1.5 rounded-lg border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                + Position
              </button>
              <div className="text-sm mt-3" style={{ color: 'var(--text-muted)' }}>
                Summe je Rechnung: <strong style={{ color: 'var(--text-primary)' }}>{euro(netCents(editor))}</strong> netto
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setEditor(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold border"
                style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}>
                Abbrechen
              </button>
              <button onClick={save} disabled={saving}
                className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

/* === UI Helpers === */

const inputStyle: React.CSSProperties = {
  background: 'var(--input-bg, var(--surface-bg))',
  border: '1px solid var(--surface-border)',
  color: 'var(--text-primary)',
  borderRadius: '8px',
  padding: '6px 10px',
  fontSize: '14px',
  width: '100%',
};

const Field: React.FC<{ label: string; full?: boolean; children: React.ReactNode }> = ({ label, full, children }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{label}</label>
    {children}
  </div>
);

const Input: React.FC<{ value: string; onChange: (v: string) => void; type?: string; placeholder?: string }> =
  ({ value, onChange, type = 'text', placeholder }) => (
    <input type={type} value={value} placeholder={placeholder}
      onChange={e => onChange(e.target.value)} style={inputStyle} />
  );

const Textarea: React.FC<{ value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }> =
  ({ value, onChange, rows = 3, placeholder }) => (
    <textarea value={value} rows={rows} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
  );

const Select: React.FC<{ value: string | number; onChange: (v: string) => void; children: React.ReactNode }> =
  ({ value, onChange, children }) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>{children}</select>
  );

export default RecurringInvoices;
